import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { EnvService } from 'gematik-form-library';
import {
  AddressDto,
  GematikProfile,
  PartnerDto,
  PartnerRoleDto,
  ChangeEmailRequestDto,
  UserActivityDto,
  ContactsDto,
  ContactTreeDto,
  ContactDto,
  ToggleContactStatusRequestDto,
  ContactType,
  IdentityRole,
  FunctionContactDto,
} from '../../models/profile.model';
import { AssignmentGroupIdentityExtendedDto } from '../../models/profile.model';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  constructor(
    private http: HttpClient,
    private env: EnvService,
  ) {}

  getProfile(): Observable<GematikProfile> {
    return this.http.get<GematikProfile>(`${this.env.backendUrl}/portal/profile`);
  }

  updateProfile(payload: GematikProfile): Observable<GematikProfile> {
    return this.http.post<GematikProfile>(`${this.env.backendUrl}/portal/profile`, payload);
  }

  updateUserActivity(payload: UserActivityDto): Observable<GematikProfile> {
    return this.http.post<GematikProfile>(`${this.env.backendUrl}/portal/profile/user-activity`, payload);
  }

  deleteProfile(): Observable<any> {
    return this.http.delete(`${this.env.backendUrl}/portal/profile`);
  }

  getAssignmentGroupIdentities(): Observable<AssignmentGroupIdentityExtendedDto[]> {
    return this.http.get<AssignmentGroupIdentityExtendedDto[]>(
      `${this.env.backendUrl}/portal/profile/assignment-group-identities`,
    );
  }

  getAssignmentGroupIdentityAddresses(id: string): Observable<AddressDto[]> {
    return this.http.get<AddressDto[]>(
      `${this.env.backendUrl}/portal/profile/assignment-group-identities/${id}/addresses`,
    );
  }

  addAssignmentGroupIdentityAddress(agiId: string, address: AddressDto): Observable<AddressDto> {
    return this.http.post<AddressDto>(
      `${this.env.backendUrl}/portal/profile/assignment-group-identities/${agiId}/addresses`,
      address,
    );
  }

  updateAssignmentGroupIdentityAddress(agiId: string, address: AddressDto): Observable<AddressDto> {
    return this.http.put<AddressDto>(
      `${this.env.backendUrl}/portal/profile/assignment-group-identities/${agiId}/addresses/${address.id}`,
      address,
    );
  }

  deleteAssignmentGroupIdentityAddress(id: string): Observable<AddressDto> {
    return this.http.delete<AddressDto>(
      `${this.env.backendUrl}/portal/profile/assignment-group-identities/addresses/${id}`,
    );
  }

  getAssignmentGroupAddresses(id: string): Observable<AddressDto[]> {
    return this.http.get<AddressDto[]>(`${this.env.backendUrl}/portal/profile/assignment-groups/${id}/addresses`);
  }

  addAssignmentGroupAddress(agId: string, address: AddressDto): Observable<AddressDto> {
    return this.http.post<AddressDto>(
      `${this.env.backendUrl}/portal/profile/assignment-groups/${agId}/addresses`,
      address,
    );
  }

  updateAssignmentGroupAddress(agId: string, address: AddressDto): Observable<AddressDto> {
    return this.http.put<AddressDto>(
      `${this.env.backendUrl}/portal/profile/assignment-groups/${agId}/addresses/${address.id}`,
      address,
    );
  }

  deleteAssignmentGroupAddress(id: string): Observable<AddressDto> {
    return this.http.delete<AddressDto>(`${this.env.backendUrl}/portal/profile/assignment-groups/addresses/${id}`);
  }

  getPartner(orgId: string): Observable<PartnerDto> {
    return this.http.get<PartnerDto>(`${this.env.backendUrl}/portal/profile/partners/${orgId}`);
  }

  updatePartner(partner: PartnerDto): Observable<PartnerDto> {
    return this.http.put<PartnerDto>(`${this.env.backendUrl}/portal/profile/partners/${partner.id}`, partner);
  }

  getPartnerRoles(orgId: string): Observable<PartnerRoleDto[]> {
    return this.http.get<PartnerRoleDto[]>(`${this.env.backendUrl}/portal/profile/partners/${orgId}/partner-roles`);
  }

  getContacts(orgId: string): Observable<ContactsDto> {
    return this.http.get<ContactsDto>(`${this.env.backendUrl}/portal/profile/contacts/${orgId}`);
  }

  getContactTree(contactId: string, contactType: string, orgId: string): Observable<ContactTreeDto> {
    return this.http.get<ContactTreeDto>(
      `${this.env.backendUrl}/portal/profile/contacts/${contactId}/${contactType}/${orgId}/tree`,
    );
  }

  toggleContactStatus(contactId: string, contactType: ContactType, orgId: string): Observable<ContactDto> {
    const payload: ToggleContactStatusRequestDto = {
      organizationId: orgId,
      contactType,
    };
    return this.http.post<ContactDto>(
      `${this.env.backendUrl}/portal/profile/contacts/${contactId}/toggle-status`,
      payload,
    );
  }

  changeEmail(payload: ChangeEmailRequestDto): Observable<any> {
    return this.http.post<any>(`${this.env.backendUrl}/portal/profile/change-email`, payload);
  }

  deleteContact(contactId: string, orgId: string): Observable<any> {
    return this.http.delete<any>(`${this.env.backendUrl}/portal/profile/contacts/${contactId}/${orgId}`);
  }

  resetContactRootPartnerAndReturnForeignContacts(contactId: string, orgId: string): Observable<any> {
    return this.http.post<any>(
      `${this.env.backendUrl}/portal/profile/contacts/${contactId}/${orgId}/reset-root-partner`,
      {},
    );
  }

  setContactRootPartner(contactId: string, orgId: string): Observable<ContactDto> {
    return this.http.post<ContactDto>(
      `${this.env.backendUrl}/portal/profile/contacts/${contactId}/${orgId}/set-root-partner`,
      {},
    );
  }

  contactExists(email: string): Observable<any> {
    return this.http.get<any>(`${this.env.backendUrl}/portal/profile/contacts/${email}/exists`);
  }

  getIdentityRoles(roleId: string): Observable<IdentityRole[]> {
    return this.http.get<IdentityRole[]>(`${this.env.backendUrl}/api/suiteCrm/partners/1/roles/${roleId}/identities`);
  }

  getFunctionContact(contactId: string, orgId: string): Observable<FunctionContactDto> {
    return this.http.get<FunctionContactDto>(
      `${this.env.backendUrl}/portal/profile/contacts/function/${contactId}/${orgId}`,
    );
  }

  createFunctionContact(payload: FunctionContactDto): Observable<FunctionContactDto> {
    return this.http.post<FunctionContactDto>(`${this.env.backendUrl}/portal/profile/contacts/function`, payload);
  }

  updateFunctionContact(contactId: string, payload: FunctionContactDto): Observable<FunctionContactDto> {
    return this.http.put<FunctionContactDto>(
      `${this.env.backendUrl}/portal/profile/contacts/function/${contactId}`,
      payload,
    );
  }
}
